import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Receipt,
  ClipboardCheck,
  BookOpen,
  Menu,
  Boxes,
  PackagePlus,
  Tag,
  Truck,
  Landmark,
  BarChart3,
  Plug,
  LifeBuoy,
  Settings,
  LogOut,
  Ticket,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useBankingAccess } from "@/hooks/useBankingAccess";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const primaryTabs = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/transactions", label: "Sales", icon: Receipt },
  { to: "/daily-close", label: "Close", icon: ClipboardCheck },
  { to: "/pricebook", label: "Pricebook", icon: BookOpen },
];

type MoreItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  requiresBankingAccess?: boolean;
};

const moreItems: MoreItem[] = [
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/reorders", label: "Purchase Orders", icon: PackagePlus },
  { to: "/reorders/suggestions", label: "Reorder Suggestions", icon: PackagePlus },
  { to: "/promotions", label: "Promotions", icon: Tag },
  { to: "/vendors", label: "Vendors", icon: Truck },
  { to: "/banking", label: "Banking & Finance", icon: Landmark, requiresBankingAccess: true },
  { to: "/lottery", label: "Lottery", icon: Ticket },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/integrations", label: "Integration Hub", icon: Plug },
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/settings", label: "Settings & Audit", icon: Settings },
];

export function MobileTabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { signOut, user } = useAuth();
  const banking = useBankingAccess();
  const visibleMoreItems = moreItems.filter(
    (item) => !item.requiresBankingAccess || banking.canView,
  );
  const nav = useNavigate();
  const { pathname } = useLocation();

  // Highlight "More" if we're on any of its routes
  const moreActive = visibleMoreItems.some((m) =>
    m.to === "/" ? pathname === "/" : pathname.startsWith(m.to),
  );

  const doSignOut = async () => {
    await signOut();
    setLogoutOpen(false);
    setMoreOpen(false);
    nav("/auth");
  };

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-sidebar/95 backdrop-blur supports-[backdrop-filter]:bg-sidebar/80 border-t border-sidebar-border pb-safe"
        aria-label="Primary"
      >
        <ul className="grid grid-cols-5 h-14">
          {primaryTabs.map((t) => (
            <li key={t.to} className="contents">
              <NavLink
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground",
                  )
                }
              >
                <t.icon className="h-5 w-5" />
                <span className="leading-none">{t.label}</span>
              </NavLink>
            </li>
          ))}
          <li className="contents">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors",
                moreActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Menu className="h-5 w-5" />
              <span className="leading-none">More</span>
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="bg-sidebar border-sidebar-border h-[85dvh] p-0 flex flex-col"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-sidebar-border text-left">
            <SheetTitle>More</SheetTitle>
            {user?.email && (
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-3 py-3">
            <ul className="space-y-0.5">
              {visibleMoreItems.map((m) => (
                <li key={m.to}>
                  <NavLink
                    to={m.to}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-md px-3 py-3 text-sm",
                        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground",
                      )
                    }
                  >
                    <m.icon className="h-4 w-4" />
                    <span>{m.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-sidebar-border p-3 pb-safe">
            <button
              onClick={() => setLogoutOpen(true)}
              className="w-full flex items-center gap-3 rounded-md px-3 py-3 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign out of StoreLedger Pro?</DialogTitle>
            <DialogDescription>
              {user?.email
                ? `Signed in as ${user.email}.`
                : "You'll be returned to the sign-in screen."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doSignOut}>Sign out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
